/**
 * ValuePropIllustration — 3 responsive cards showing the Aavaa loop:
 *   1. Your child plays (with camera observing)
 *   2. Aavaa analyzes the feed
 *   3. You get a clip-backed summary (phone mockup — fully contained within card)
 *
 * Layout: stacks vertically on mobile (single column, bigger cards),
 * horizontal on lg+ screens.
 */
export default function ValuePropIllustration() {
  return (
    <div className="w-full" data-testid="value-prop-illustration">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6 relative">
        <StageCard number="01" title="Your child plays" subtitle="Your existing home camera quietly records the day.">
          <RoomIllustration />
        </StageCard>

        <StageCard number="02" title="Aavaa analyzes the feed" subtitle="AI watches for the behaviors and signals you chose.">
          <AnalyzeIllustration />
        </StageCard>

        <StageCard number="03" title="You get a summary, backed by real clips" subtitle="Every observation links to the exact moment it's based on.">
          <SummaryIllustration />
        </StageCard>
      </div>
    </div>
  );
}

/* ================= Shared card shell ================= */
function StageCard({ number, title, subtitle, children }) {
  return (
    <div className="bg-white rounded-[28px] border border-[#E2DFD8]/70 p-6 sm:p-8 overflow-hidden relative shadow-[0_4px_24px_-8px_rgba(47,54,48,0.06)] hover:shadow-[0_8px_30px_-12px_rgba(47,54,48,0.1)] transition-shadow">
      <div className="flex items-center gap-2 mb-4">
        <span className="font-outfit text-xs text-[#D4A373] tracking-[0.15em]">{number}</span>
        <div className="h-px bg-[#E2DFD8] flex-1" />
      </div>

      {/* Illustration zone — fixed aspect so all cards align on desktop */}
      <div className="aspect-[4/3] w-full bg-[#FAFAF7] rounded-[20px] border border-[#E2DFD8]/50 mb-5 relative overflow-hidden flex items-center justify-center">
        {children}
      </div>

      <h3 className="font-outfit text-lg sm:text-xl font-medium text-[#2F3630] tracking-tight mb-1.5">{title}</h3>
      <p className="font-manrope text-sm text-[#6B7264] leading-relaxed">{subtitle}</p>
    </div>
  );
}

/* ================= Stage 1 — Room + child + camera ================= */
function RoomIllustration() {
  return (
    <svg
      viewBox="0 0 280 210"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Illustration of a child playing on the floor while a small home camera observes from above."
    >
      <defs>
        <linearGradient id="rm-floor" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#F3EFE6" />
          <stop offset="100%" stopColor="#E2DFD8" />
        </linearGradient>
        <radialGradient id="rm-spot" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#D4A373" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#D4A373" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Floor */}
      <rect x="0" y="150" width="280" height="60" fill="url(#rm-floor)" />

      {/* Warm light pool */}
      <ellipse cx="160" cy="165" rx="110" ry="38" fill="url(#rm-spot)" />

      {/* Mounted camera */}
      <g transform="translate(40,24)">
        <rect x="19" y="-10" width="4" height="10" fill="#B8AE98" />
        <rect x="0" y="0" width="42" height="24" rx="7" fill="#8E9B82" />
        <circle cx="14" cy="12" r="7" fill="#2F3630" />
        <circle cx="14" cy="12" r="3.5" fill="#A7B399" />
        <circle cx="30" cy="7" r="1.8" fill="#D4A373">
          <animate attributeName="opacity" values="0.4;1;0.4" dur="2s" repeatCount="indefinite" />
        </circle>
      </g>

      {/* Viewing cone */}
      <path d="M 63 52 L 25 190 L 240 190 Z" fill="#8E9B82" opacity="0.08" />
      <path d="M 63 52 L 25 190 L 240 190 Z" fill="none" stroke="#8E9B82" strokeOpacity="0.35" strokeDasharray="3 4" />

      {/* Blocks + toys on floor */}
      <rect x="70" y="168" width="16" height="12" fill="#D4A373" rx="2" />
      <rect x="88" y="172" width="14" height="10" fill="#8E9B82" rx="2" />
      <rect x="104" y="168" width="12" height="14" fill="#C27E70" rx="2" />

      {/* Child */}
      <g transform="translate(135,115)">
        {/* Body */}
        <ellipse cx="22" cy="68" rx="28" ry="6" fill="#2F3630" opacity="0.13" />
        <path d="M 0 62 Q 0 30 22 26 Q 44 30 44 62 Z" fill="#D4A373" />
        {/* Head */}
        <circle cx="22" cy="18" r="18" fill="#EED3B5" />
        {/* Hair */}
        <path d="M 7 14 Q 22 0 37 14 Q 34 7 22 5 Q 10 7 7 14 Z" fill="#6B4F3C" />
        {/* Glasses */}
        <circle cx="16" cy="20" r="4" fill="none" stroke="#2F3630" strokeWidth="1.2" />
        <circle cx="28" cy="20" r="4" fill="none" stroke="#2F3630" strokeWidth="1.2" />
        <line x1="20" y1="20" x2="24" y2="20" stroke="#2F3630" strokeWidth="1.2" />
        {/* Smile */}
        <path d="M 17 26 Q 22 29 27 26" fill="none" stroke="#2F3630" strokeWidth="1.2" strokeLinecap="round" />
      </g>
    </svg>
  );
}

/* ================= Stage 2 — Aavaa analyzing ================= */
function AnalyzeIllustration() {
  return (
    <svg
      viewBox="0 0 280 210"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Aavaa's AI engine analyzing video feed, shown as a glowing sage sphere with rotating dotted ring and process markers."
    >
      <defs>
        <linearGradient id="an-sphere" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#A7B399" />
          <stop offset="100%" stopColor="#7A8A6E" />
        </linearGradient>
        <radialGradient id="an-halo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#D4A373" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#D4A373" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Background halo */}
      <circle cx="140" cy="85" r="85" fill="url(#an-halo)" />

      {/* Sphere */}
      <circle cx="140" cy="85" r="46" fill="url(#an-sphere)" />
      {/* Inner rotating dotted ring */}
      <circle cx="140" cy="85" r="34" fill="none" stroke="#FFFFFF" strokeWidth="3.5" strokeDasharray="4 3">
        <animateTransform attributeName="transform" type="rotate" from="0 140 85" to="360 140 85" dur="14s" repeatCount="indefinite" />
      </circle>
      {/* Sparkle */}
      <path d="M 140 70 L 144 82 L 156 85 L 144 88 L 140 100 L 136 88 L 124 85 L 136 82 Z" fill="#D4A373" />
      {/* Orbit dots — 3 evenly spaced at 120°, radius 52 from (140,85) */}
      <g>
        <circle cx="140" cy="33" r="5" fill="#D4A373" stroke="#FAFAF7" strokeWidth="2.5" />
        <circle cx="95" cy="111" r="5" fill="#D4A373" stroke="#FAFAF7" strokeWidth="2.5" />
        <circle cx="185" cy="111" r="5" fill="#D4A373" stroke="#FAFAF7" strokeWidth="2.5" />
      </g>

      {/* Process ticker lines */}
      <g transform="translate(60,155)">
        {[0, 1, 2].map((i) => (
          <g key={i} transform={`translate(0, ${i * 16})`}>
            <circle cx="6" cy="6" r="4" fill="#8E9B82" opacity={0.35 + i * 0.22} />
            <rect x="18" y="3.5" width={150 - i * 20} height="5" rx="2.5" fill="#E2DFD8" />
          </g>
        ))}
      </g>
    </svg>
  );
}

/* ================= Stage 3 — Phone with summary (contained) ================= */
function SummaryIllustration() {
  return (
    <svg
      viewBox="0 0 280 210"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="A parent holding a phone that shows Aavaa's daily summary of their child's behaviors with clip thumbnails."
    >
      <defs>
        <linearGradient id="sm-bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F3EFE6" />
          <stop offset="100%" stopColor="#FAFAF7" />
        </linearGradient>
      </defs>

      <rect width="280" height="210" fill="url(#sm-bg)" opacity="0" />

      {/* Parent figure on left */}
      <g transform="translate(26,65)">
        <ellipse cx="26" cy="120" rx="34" ry="6" fill="#2F3630" opacity="0.1" />
        <path d="M 0 112 Q 0 66 26 64 Q 52 66 52 112 Z" fill="#8E9B82" />
        <circle cx="26" cy="30" r="22" fill="#EED3B5" />
        <path d="M 8 26 Q 10 8 26 6 Q 44 8 46 26 Q 40 18 26 17 Q 12 18 8 26 Z" fill="#3D2A1E" />
        <path d="M 20 36 Q 26 40 32 36" stroke="#2F3630" strokeWidth="1.3" fill="none" strokeLinecap="round" />
        <circle cx="20" cy="30" r="1.5" fill="#2F3630" />
        <circle cx="32" cy="30" r="1.5" fill="#2F3630" />
      </g>

      {/* Phone (fits inside viewBox) */}
      <g transform="translate(122,22)">
        <rect x="0" y="0" width="130" height="175" rx="18" fill="#1F2420" />
        <rect x="6" y="6" width="118" height="163" rx="13" fill="#FAFAF7" />

        {/* Header */}
        <rect x="14" y="16" width="60" height="7" rx="2.5" fill="#2F3630" />
        <rect x="14" y="27" width="40" height="4" rx="2" fill="#D4A373" />

        {/* Overview card */}
        <rect x="13" y="40" width="104" height="26" rx="5" fill="#FFFFFF" stroke="#E2DFD8" strokeWidth="0.5" />
        <rect x="18" y="46" width="32" height="4" rx="1.5" fill="#2F3630" />
        <rect x="18" y="53" width="84" height="3" rx="1.5" fill="#6B7264" opacity="0.5" />
        <rect x="18" y="58.5" width="66" height="3" rx="1.5" fill="#6B7264" opacity="0.5" />

        {/* Observation row 1 */}
        <g transform="translate(13, 72)">
          <rect width="104" height="30" rx="5" fill="#FFFFFF" stroke="#E2DFD8" strokeWidth="0.5" />
          <rect x="5" y="5" width="56" height="3.5" rx="1.5" fill="#6B7264" opacity="0.5" />
          <rect x="5" y="11" width="40" height="4" rx="1.5" fill="#2F3630" />
          <rect x="92" y="5" width="7" height="14" rx="3" fill="#8E9B82" />
          {/* clip thumbs */}
          <rect x="5" y="19" width="14" height="8" rx="1.5" fill="#8E9B82" />
          <rect x="21" y="19" width="14" height="8" rx="1.5" fill="#D4A373" />
          <rect x="37" y="19" width="14" height="8" rx="1.5" fill="#A7B399" />
        </g>

        {/* Observation row 2 */}
        <g transform="translate(13, 108)">
          <rect width="104" height="30" rx="5" fill="#FFFFFF" stroke="#E2DFD8" strokeWidth="0.5" />
          <rect x="5" y="5" width="50" height="3.5" rx="1.5" fill="#6B7264" opacity="0.5" />
          <rect x="5" y="11" width="44" height="4" rx="1.5" fill="#2F3630" />
          <rect x="92" y="5" width="7" height="14" rx="3" fill="#D4A373" />
          {/* clip thumbs */}
          <rect x="5" y="19" width="14" height="8" rx="1.5" fill="#D4A373" />
          <rect x="21" y="19" width="14" height="8" rx="1.5" fill="#E7C19A" />
          <rect x="37" y="19" width="14" height="8" rx="1.5" fill="#8E9B82" />
        </g>

        {/* Bottom dot */}
        <circle cx="65" cy="154" r="2" fill="#D4A373" />
      </g>

      {/* Small sparkle badge */}
      <g transform="translate(222,22)">
        <rect x="0" y="0" width="56" height="18" rx="9" fill="#FFFFFF" stroke="#E2DFD8" />
        <circle cx="10" cy="9" r="3" fill="#D4A373" />
        <rect x="17" y="7" width="32" height="4" rx="1.5" fill="#6B7264" opacity="0.7" />
      </g>
    </svg>
  );
}
